import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { type SubmitHandler, useForm } from "react-hook-form"
import type { ActionFunctionArgs } from "react-router"
import { redirect } from "react-router"
import { Link, useActionData, useNavigation, useSubmit } from "react-router"

import { type LoginFormValues, loginSchema } from "~/lib/schemas/auth"

import { Button } from "~/components/Button"
import { Input } from "~/components/Input"
import { Card, CardContent, CardFooter, CardHeader } from "~/components/ui/card"
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "~/components/ui/form"
import { Typography } from "~/components/ui/typography"
import { authClient } from "~/lib/auth-client"
import { authProxy } from "~/lib/auth-proxy"

// is server side

// add loader and check if user is already logged in
export async function loader({ request }: { request: Request }) {
	const session = await authProxy.api.getSession({
		headers: request.headers,
	})
	if (session?.user.id) {
		throw redirect("/dashboard/backtests")
	}
	return null
}

// For server-side validation and actions
export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const email = formData.get("email")
	const password = formData.get("password")
	const rememberMe = formData.get("rememberMe") === "true"

	// Validate input on the server side
	try {
		loginSchema.parse({ email, password, rememberMe })
	} catch {
		return json({ error: "Invalid form data" }, { status: 400 })
	}

	try {
		// TODO: Implement actual authentication logic
		// This is a placeholder for now
		// For demonstration purposes, let's simulate a successful login
		return redirect("/dashboard")
	} catch {
		return new Response("Invalid email or password", { status: 401 })
	}
}

export default function LoginPage() {
	const actionData = useActionData<{ error?: string }>()
	const navigation = useNavigation()
	const isSubmitting = navigation.state === "submitting"
	const [isLoading, setIsLoading] = useState(false)
	const submit = useSubmit()

	const form = useForm<LoginFormValues>({
		resolver: zodResolver(loginSchema),
		defaultValues: {
			email: "",
			password: "",
			rememberMe: false,
		},
	})

	const [showPassword, setShowPassword] = useState(false)

	const onSubmit: SubmitHandler<LoginFormValues> = async (data) => {
		try {
			setIsLoading(true)
			const { error } = await authClient.signIn.email({
				email: data.email,
				password: data.password,
				callbackURL: "/dashboard",
			})
			if (error) {
				form.setError("root", {
					type: "manual",
					message: error.message || "Invalid email or password",
				})
				setIsLoading(false)
				return
			}

			const formData = new FormData()
			formData.append("email", data.email)
			formData.append("password", data.password)
			if (data.rememberMe) {
				formData.append("rememberMe", "true")
			}
			submit(formData, { method: "post" })
		} catch {
			form.setError("root", {
				type: "manual",
				message: "Invalid email or password",
			})
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="min-h-fit flex flex-col items-center justify-center bg-[#F6F8FB] px-2 py-2 md:py-6 font-sans">
			<Card className="w-full max-w-md min-h-[520px] rounded-3xl bg-white shadow-lg border-0 p-0 pt-2 md:pt-3 md:px-4 sm:px-2 text-[15px] md:text-[16px] mt-2 md:mt-4 font-noto">
				<CardHeader className="bg-white rounded-t-3xl pb-3">
					<Typography
						variant="h2"
						align="center"
						fontFamily="manrope"
						className="mb-1 text-2xl md:text-3xl font-manrope"
					>
						Sign in to your account
					</Typography>
					<Typography
						variant="small"
						color="muted"
						align="center"
						fontFamily="noto"
						className="mt-1 font-noto"
					>
						Enter your credentials to access your account
					</Typography>
				</CardHeader>
				<CardContent className="rounded-b-3xl bg-white px-2 md:px-4 font-noto">
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 font-noto">
							{/* Display form-level errors */}
							{(form.formState.errors.root?.message || actionData?.error) && (
								<div className="p-3 bg-red-50 border border-red-200 rounded-md font-noto">
									<Typography
										variant="xs"
										color="destructive"
										fontFamily="noto"
										className="font-noto"
									>
										{form.formState.errors.root?.message || actionData?.error}
									</Typography>
								</div>
							)}
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem className="font-noto">
										<FormLabel htmlFor="email" className="sr-only font-noto">
											E-posta
										</FormLabel>
										<FormControl>
											<Input
												id="email"
												placeholder="E-posta"
												type="email"
												autoComplete="email"
												className="h-12 rounded-2xl border-2 bg-[#F6F8FB] border-gray-200 focus:border-[#2954D6] focus:ring-0 text-base px-4 font-medium font-noto
                            disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400
                            data-[error=true]:border-red-500 data-[error=true]:bg-red-50"
												{...field}
											/>
										</FormControl>
										<FormMessage className="text-base sm:text-md font-medium text-red-600 font-noto" />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem className="font-noto">
										<FormLabel htmlFor="password" className="sr-only font-noto">
											Şifre
										</FormLabel>
										<div className="relative">
											<FormControl>
												<Input
													id="password"
													placeholder="Şifre"
													type={showPassword ? "text" : "password"}
													autoComplete="current-password"
													className="h-12 rounded-2xl border-2 bg-[#F6F8FB] border-gray-200 focus:border-[#2954D6] focus:ring-0 text-base px-4 font-medium pr-12 font-noto
                              disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400
                              data-[error=true]:border-red-500 data-[error=true]:bg-red-50"
													{...field}
												/>
											</FormControl>
											<button
												type="button"
												className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg font-noto"
												onClick={() => setShowPassword(!showPassword)}
												tabIndex={-1}
											>
												{showPassword ? (
													<span role="img" aria-label="Hide" className="font-noto">
														🙈
													</span>
												) : (
													<span role="img" aria-label="Show" className="font-noto">
														👁️
													</span>
												)}
											</button>
										</div>
										<FormMessage className="text-base sm:text-md font-medium text-red-600 font-noto" />
									</FormItem>
								)}
							/>
							<div className="flex items-center justify-between font-noto">
								<div />
								<Link
									to="/auth/reset-password"
									className="text-black text-sm font-medium hover:underline font-noto"
								>
									Forgot your password?
								</Link>
							</div>
							<Button
								type="submit"
								className="w-full h-12 rounded-2xl bg-[#2954D6] hover:bg-[#1d3e9e] text-base font-extrabold mt-2 font-noto"
								disabled={isSubmitting || isLoading}
							>
								{isSubmitting || isLoading ? "Signing in..." : "Sign In"}
							</Button>
							<div className="flex items-center my-2 font-noto">
								<div className="flex-grow border-t border-gray-200" />
								<span className="mx-3 text-gray-400 text-sm font-noto">or sign in with</span>
								<div className="flex-grow border-t border-gray-200" />
							</div>
							<div className="flex justify-center gap-3 mt-2 font-noto">
								<button
									type="button"
									className="bg-white border border-gray-200 rounded-xl p-3 flex items-center justify-center shadow-sm hover:bg-gray-50 transition font-noto"
								>
									<img src="/icons/google.svg" alt="Google" className="w-6 h-6" />
								</button>
								<button
									type="button"
									className="bg-white border border-gray-200 rounded-xl p-3 flex items-center justify-center shadow-sm hover:bg-gray-50 transition font-noto"
								>
									<img src="/icons/apple.svg" alt="Apple" className="w-6 h-6" />
								</button>
								<button
									type="button"
									className="bg-white border border-gray-200 rounded-xl p-3 flex items-center justify-center shadow-sm hover:bg-gray-50 transition font-noto"
								>
									<img src="/icons/facebook.svg" alt="Facebook" className="w-6 h-6" />
								</button>
								<button
									type="button"
									className="bg-white border border-gray-200 rounded-xl p-3 flex items-center justify-center shadow-sm hover:bg-gray-50 transition font-noto"
								>
									<img src="/icons/user-group.svg" alt="Other" className="w-6 h-6" />
								</button>
							</div>
						</form>
					</Form>
				</CardContent>
				<CardFooter className="bg-white rounded-b-3xl pb-6 pt-3 mt-1 font-noto">
					<Typography
						variant="sm"
						color="muted"
						align="center"
						fontFamily="noto"
						className="w-full leading-snug font-noto"
					>
						Don&apos;t have an account?{" "}
						<Link to="/signup" className="font-bold text-[#2954D6] hover:underline font-noto">
							Sign up
						</Link>
					</Typography>
				</CardFooter>
			</Card>
		</div>
	)
}
